import {
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useId,
  useState
} from 'react'
import clsx from 'clsx'

import { ValidationResponseTypes, ValidationTypes } from 'shared/ui/form/inputs'

import styles from './Checkbox.module.scss'

export interface CheckboxProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    | 'onChange'
    | 'onBlur'
    | 'onFocus'
    | 'onKeyDown'
    | 'role'
    | 'tabIndex'
    | 'id'
    | 'aria-label'
    | 'aria-labelledby'
    | 'aria-describedby'
    | 'aria-required'
  > {
  onChange?: (name: string, value: boolean) => void
  onBlur?: () => void
  onFocus?: () => void
  validate?: (
    name: string,
    callback: (v: ValidationResponseTypes) => void
  ) => void
  label?: string | ReactNode
  /** Rendered outside the <label> (e.g. a link that must not toggle the checkbox) */
  labelAddon?: ReactNode
  name: string
  value?: boolean
  disabled?: boolean
  required?: boolean
  noMargin?: boolean
  paddingLeft?: boolean
  /** Applied to the native <input>; the error id is `${id}-error` */
  id?: string
  className?: string
  invalid?: string
  ref?: Ref<HTMLInputElement>
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

const Checkbox = ({
  onChange,
  onBlur,
  onFocus,
  validate,
  label,
  name,
  disabled,
  required,
  noMargin,
  id,
  paddingLeft,
  value,
  invalid,
  labelAddon,
  ref,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  className,
  ...props
}: CheckboxProps) => {
  const [touched, setTouched] = useState(false)

  const generatedId = useId()
  const inputId = id || `checkbox-${generatedId}`

  const [{ valid }, _validate] = useState<ValidationTypes>({
    valid: !validate,
    error: ''
  })

  // Memoize the validation callback to prevent infinite loops
  const validationCallback = useCallback((v: ValidationResponseTypes) => {
    _validate({ valid: v.valid, error: v.message })
  }, [])

  useEffect(() => {
    if (typeof validate === 'function') {
      validate(name, validationCallback)
    }
  }, [value, validate, name, validationCallback])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (typeof onChange === 'function') {
      onChange(name, e.target.checked)
    }
  }

  const handleBlur = () => {
    setTouched(true)
    if (typeof onBlur === 'function') {
      onBlur()
    }
  }

  const hasError = !!invalid || (!valid && touched && !disabled)

  return (
    <div
      {...props}
      className={clsx(
        styles.wrapper,
        {
          [styles.noMargin]: noMargin,
          [styles.paddingLeft]: paddingLeft,
          [styles.withAddon]: !!labelAddon
        },
        className
      )}
    >
      <label
        htmlFor={inputId}
        className={clsx(styles.label, {
          [styles.labelDisabled]: disabled,
          [styles.labelError]: hasError
        })}
      >
        {required ? (
          <div style={{ display: 'inline-block' }}>
            {label}
            <span className={styles.requiredStar} aria-hidden='true'>
              &nbsp;*
            </span>
          </div>
        ) : (
          label
        )}
        <input
          id={inputId}
          type='checkbox'
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={onFocus}
          disabled={disabled}
          checked={!!value}
          required={required}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-invalid={hasError || undefined}
          aria-describedby={ariaDescribedBy}
          ref={ref}
          name={name}
          className={styles.input}
        />
        <span className={styles.mark} aria-hidden='true' />
      </label>
      {labelAddon && <div className={styles.labelAddon}>{labelAddon}</div>}
    </div>
  )
}

export default Checkbox
